// network-identity-semantic-v2: the single presentation resolver for "what kind of network is this" —
// replacing five independent, occasionally-disagreeing implementations that used to live in
// homeVerdict.ts (ipAttributeLabel/networkTypeLabel), ipSource.ts (getIpSourceInfo), ResultView.tsx's
// inline flag-only logic, and Ipv6LeakTestPage.tsx's inline logic.
//
// Three axes, deliberately kept separate rather than collapsed into one "IP type" (spec: a competitor's
// single-label verdict conflates access network, infrastructure and anonymity into one string):
//   - access:         what kind of network this address belongs to (residential/business_access/
//                      mobile/.../unknown) — risk.networkClassification / classification.networkType.
//   - infrastructure: is this datacenter/hosting/public infrastructure, or an ordinary access network —
//                      flags.isDatacenter/isHosting + the public-infrastructure override already folded
//                      into classification.networkType server-side (PublishedNetworkTypeResolver).
//   - connection:     is the current connection anonymized (vpn/proxy/residential_proxy/tor/relay) or
//                      direct — risk.connectionClassification / classification.connectionTypeV2.
//
// Every field here is read from the canonical DTO the backend already publishes; this module computes
// no new classification of its own, only how to label and gate the ones that already exist. In
// particular: `null` (not yet examined) is never treated as `false` (checked, not found) — see
// InfrastructureIdentity's `state`.
import { homeShellString, type getStrings } from "../i18n/strings";
import type { PublicIpRiskResultDto } from "../types/dto";
import { DEFINITE_CONFIDENCE_THRESHOLD } from "./networkClassification";

type Strings = ReturnType<typeof getStrings>;

/** "confirmed": confidence cleared DEFINITE_CONFIDENCE_THRESHOLD. "tentative": a specific type is the
 * best estimate, just not confidently enough to state as fact yet. "unknown": no meaningful type at all. */
export type IdentityConfidenceState = "confirmed" | "tentative" | "unknown";

/** "detected"/"not_detected": explicitly checked, one way or the other. "unknown": never examined this
 * round — must never be presented the same way as "not_detected" (spec: null ≠ false). */
export type InfrastructureState = "detected" | "not_detected" | "unknown";

export interface AccessIdentity {
  /** One of NetworkTypes, or "unknown". */
  type: string;
  label: string | null;
  confidence: number | null;
  state: IdentityConfidenceState;
}

export interface InfrastructureIdentity {
  type: "public_infrastructure" | "datacenter" | "hosting" | "ordinary_access" | "unknown";
  label: string | null;
  state: InfrastructureState;
}

export interface ConnectionIdentity {
  /** One of ConnectionTypes, or "unknown". */
  type: string;
  label: string | null;
  confidence: number | null;
  state: IdentityConfidenceState;
}

export interface NetworkIdentityPresentation {
  access: AccessIdentity;
  infrastructure: InfrastructureIdentity;
  connection: ConnectionIdentity;
}

function accessTypeLabel(strings: Strings, type: string): string | null {
  return strings.networkTypeNames?.[type] ?? type;
}

function connectionTypeLabel(strings: Strings, type: string): string | null {
  return strings.connectionTypeNames?.[type] ?? strings.networkTypeNames?.[type] ?? type;
}

/** Appends the "(low confidence)" marker for a tentative best-estimate label — never hides it. */
export function withTentativeSuffix(label: string, state: IdentityConfidenceState, strings: Strings): string {
  return state === "tentative" ? `${label}${strings.networkIdentityTentativeSuffix ?? ""}` : label;
}

function resolveAccess(result: PublicIpRiskResultDto, strings: Strings): AccessIdentity {
  const classification = result.risk.networkClassification;
  // Published-first: the field every other public surface (paid API, CLI, share card) renders, which
  // already carries the server-side public_infrastructure override (PublishedNetworkTypeResolver).
  // Falls back to the raw classification, then the deprecated legacy label, for older snapshots.
  const published = result.classification.networkType ?? classification?.type ?? result.classification.connectionType;

  if (!published || published === "unknown") {
    return {
      type: "unknown",
      label: classification ? accessTypeLabel(strings, "unknown") : null,
      confidence: classification?.confidence ?? null,
      state: "unknown",
    };
  }

  // public_infrastructure is only ever published once the backend has reliably established an
  // infrastructure profile (risk.infrastructureProfile, confidence-gated server-side) — never a
  // confidence judgment this resolver makes itself.
  if (published === "public_infrastructure") {
    return { type: published, label: accessTypeLabel(strings, published), confidence: null, state: "confirmed" };
  }

  const confidence = classification && classification.type === published ? classification.confidence : null;
  const state: IdentityConfidenceState =
    confidence !== null && confidence >= DEFINITE_CONFIDENCE_THRESHOLD ? "confirmed" : "tentative";
  return { type: published, label: accessTypeLabel(strings, published), confidence, state };
}

function resolveInfrastructure(result: PublicIpRiskResultDto, strings: Strings): InfrastructureIdentity {
  if (result.classification.networkType === "public_infrastructure") {
    return { type: "public_infrastructure", label: accessTypeLabel(strings, "public_infrastructure"), state: "detected" };
  }

  // isDatacenter carries the full detected/not_detected/unknown fidelity (risk-v4.24); isHosting can
  // still read null even after a direct detection (see NetRiskScan_API_DTO_Semantics_risk-v4.15.x.md),
  // so a positive read on EITHER flag is trusted, but only isDatacenter's explicit false is trusted for
  // the negative "checked and not found" state — isHosting being null there must not downgrade it.
  if (result.flags.isDatacenter === true) {
    return { type: "datacenter", label: accessTypeLabel(strings, "datacenter"), state: "detected" };
  }
  if (result.flags.isHosting === true) {
    return { type: "hosting", label: accessTypeLabel(strings, "hosting"), state: "detected" };
  }
  if (result.flags.isDatacenter === false) {
    return { type: "ordinary_access", label: strings.infrastructureNotDatacenter ?? null, state: "not_detected" };
  }
  return { type: "unknown", label: null, state: "unknown" };
}

// A pre-classification snapshot (or a provider round that only ever populated the raw flags) still
// carries real positive evidence on IpRiskFlags — read it in the same severity order
// NetworkClassificationEngine's ConnectionCandidates uses (Tor > Proxy > Vpn > Relay), and only ever
// for a POSITIVE flag. This is never used to infer Direct: an all-false/all-null flag set here still
// falls through to "unknown" below, exactly as before (spec: no exclusion inference).
function flagsConnectionFallback(result: PublicIpRiskResultDto): string | null {
  if (result.flags.isTor === true) return "tor";
  if (result.flags.isProxy === true) return "proxy";
  if (result.flags.isVpn === true) return "vpn";
  if (result.flags.isRelay === true) return "relay";
  return null;
}

function resolveConnection(result: PublicIpRiskResultDto, strings: Strings): ConnectionIdentity {
  const classification = result.risk.connectionClassification;
  const published = result.classification.connectionTypeV2 ?? classification?.type ?? flagsConnectionFallback(result);

  if (!published || published === "unknown") {
    return { type: "unknown", label: null, confidence: classification?.confidence ?? null, state: "unknown" };
  }

  if (classification && classification.type === published) {
    const state: IdentityConfidenceState =
      classification.confidence >= DEFINITE_CONFIDENCE_THRESHOLD ? "confirmed" : "tentative";
    return { type: published, label: connectionTypeLabel(strings, published), confidence: classification.confidence, state };
  }

  // Reached only via the flags fallback above — a confirmed boolean flag is itself a definite fact,
  // not a probabilistic classification, so there is no confidence number to attach or gate on.
  return { type: published, label: connectionTypeLabel(strings, published), confidence: null, state: "confirmed" };
}

export function resolveNetworkIdentityPresentation(
  result: PublicIpRiskResultDto,
  strings: Strings,
): NetworkIdentityPresentation {
  return {
    access: resolveAccess(result, strings),
    infrastructure: resolveInfrastructure(result, strings),
    connection: resolveConnection(result, strings),
  };
}

// Kept for callers that only want the tile label, not the full presentation object — the same shape
// homeShellString()'s callers already expect (a resolved label or null when nothing to show).
export function accessLabel(result: PublicIpRiskResultDto, strings: Strings): string | null {
  const access = resolveAccess(result, strings);
  return access.label ? withTentativeSuffix(access.label, access.state, strings) : null;
}

export function connectionLabel(result: PublicIpRiskResultDto, strings: Strings): string | null {
  const connection = resolveConnection(result, strings);
  return connection.label ? withTentativeSuffix(connection.label, connection.state, strings) : null;
}

// Re-exported so components that only need home-shell string lookups don't have to import both
// modules — homeShellString itself is unrelated to network identity, this is purely a convenience.
export { homeShellString };
